import React from 'react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-brand-500 to-brand-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Terms & Conditions</h1>
          <p className="text-xl text-brand-100">Please read these terms carefully before using our services</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-8">
          
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms & Conditions constitute a legally binding agreement between you ("User") and 99Prop Capital ("Company", "we", "us", or "our"), the owner and operator of www.99prop.com ("Platform").
            </p>
            <p className="text-gray-700 leading-relaxed">
              By accessing, browsing, submitting information, or using this Platform, the User agrees to be legally bound by these Terms & Conditions.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If the User does not agree with any part of these Terms, they must discontinue use of the Platform immediately.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Use of this Platform constitutes full and unconditional acceptance of these Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">2. Nature of Services</h2>
            <p className="text-gray-700 leading-relaxed">
              99prop.com is owned and operated by <span className="font-semibold">99Prop Capital</span>, a real estate valuation and property acquisition company.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">The Platform provides:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Property valuation estimates</li>
              <li>Market trend analysis</li>
              <li>Buyer strategy reports</li>
              <li>ROI illustrations and projections</li>
              <li>Area growth insights</li>
              <li>General documentation guidance</li>
            </ul>
            <p className="text-gray-700 leading-relaxed italic">
              All services are informational and analytical in nature only.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The Platform does not function as a public listing marketplace.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">The Company does not provide:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Legal advice</li>
              <li>Financial advisory services</li>
              <li>Tax advisory services</li>
              <li>SEBI-registered investment advisory services</li>
              <li>Certified valuation certification services</li>
            </ul>
            <p className="text-gray-700 leading-relaxed italic">
              Any information provided is for general informational purposes only.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">3. Valuation Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed font-semibold">
              All property valuation estimates provided on the Platform:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Are generated using AI-powered analytical tools and third-party aggregated data sources</li>
              <li>Are based on publicly available information, historical trends, and algorithmic estimation models</li>
              <li>Reflect indicative market analysis only</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">The valuation range shown:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Does not represent a guaranteed selling price</li>
              <li>Does not constitute a binding offer</li>
              <li>Does not obligate any buyer or seller</li>
              <li>Does not guarantee transaction closure at the estimated value</li>
              <li>Does not create any contractual commitment</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">
              Final transaction value of any property is solely determined by:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>The property owner</li>
              <li>Market demand and supply</li>
              <li>Physical inspection</li>
              <li>Legal due diligence</li>
              <li>Negotiation between parties</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">
              99Prop Capital shall not be held responsible for any variance between estimated and actual transaction value.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">4. No Financial or Investment Advice</h2>
            <p className="text-gray-700 leading-relaxed">
              All strategy outputs, ROI projections, growth forecasts, scoring models, and recommendations provided on this Platform:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Are for informational purposes only</li>
              <li>Do not constitute financial advice</li>
              <li>Do not constitute investment advisory services</li>
              <li>Do not constitute tax advice</li>
              <li>Do not constitute legal advice</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">
              Nothing on this Platform shall be interpreted as a recommendation to buy, sell, hold, or invest in any property.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Users are strongly advised to consult qualified financial advisors, chartered accountants, registered valuers, and legal professionals before making any real estate decision.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              All decisions made by Users are at their sole discretion and risk.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">5. Buyer Advisory Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed">
              The Buyer Decision Tool provides analytical insights based on:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>User inputs</li>
              <li>Risk profile selection</li>
              <li>Budget range</li>
              <li>Market data trends</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">These outputs:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Do not guarantee capital appreciation</li>
              <li>Do not guarantee rental yield</li>
              <li>Do not guarantee liquidity</li>
              <li>Do not guarantee possession timelines</li>
              <li>Do not guarantee return on investment</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Real estate markets are subject to economic, regulatory, infrastructure, political, and demand fluctuations.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              99Prop Capital shall not be liable for any investment outcomes.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">6. AI & Data Usage Disclosure</h2>
            <p className="text-gray-700 leading-relaxed">
              Valuations and recommendations may be generated using:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>AI-powered analytical tools</li>
              <li>Algorithm-based scoring models</li>
              <li>Statistical estimation frameworks</li>
              <li>Market trend analytics</li>
              <li>Third-party aggregated data sources</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">While reasonable efforts are made to maintain accuracy:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>No warranty is provided regarding completeness</li>
              <li>No representation is made regarding absolute accuracy</li>
              <li>Data may be subject to updates and revisions without prior notice</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Users acknowledge that analytical outputs may contain statistical, technical, or computational variations.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">7. No Guarantee of Sale or Purchase</h2>
            <p className="text-gray-700 leading-relaxed font-semibold">99Prop Capital does not guarantee:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Sale of property within a specific timeframe</li>
              <li>Availability of buyers</li>
              <li>Market liquidity</li>
              <li>Property appreciation</li>
              <li>ROI realization</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Any timelines, case studies, performance references, or transaction indicators are illustrative and subject to market conditions.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">8. Documentation & Legal Due Diligence</h2>
            <p className="text-gray-700 leading-relaxed">
              99Prop Capital may provide general documentation guidance.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">However:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>The Company does not independently verify title authenticity</li>
              <li>The Company does not certify ownership</li>
              <li>The Company does not conduct legal due diligence</li>
              <li>The Company does not guarantee regulatory compliance</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">
              It is the sole responsibility of the buyer and seller to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Verify property documents</li>
              <li>Conduct independent legal due diligence</li>
              <li>Confirm encumbrances</li>
              <li>Confirm approvals and permissions</li>
              <li>Review title history</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold">
              The Company shall not be liable for documentation errors, omissions, fraud, or misrepresentations by third parties.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">9. Transaction Structure & Role of the Company</h2>
            <p className="text-gray-700 leading-relaxed">
              Unless expressly agreed through a separate written agreement, 99Prop Capital:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Is not a party to transactions between independent buyers and sellers</li>
              <li>Does not assume fiduciary responsibility</li>
              <li>Does not guarantee performance of either party</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Where the Company directly acquires or books a property under a separate agreement, such transaction shall be governed strictly by that executed agreement.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">10. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Under no circumstances shall 99Prop Capital, its directors, employees, affiliates, partners, or representatives be liable for:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Financial losses</li>
              <li>Investment losses</li>
              <li>Opportunity losses</li>
              <li>Market fluctuations</li>
              <li>Transaction delays</li>
              <li>Data inaccuracies</li>
              <li>Technical errors</li>
              <li>Human negotiation errors</li>
              <li>Algorithmic estimation deviations</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">
              Use of the Platform is entirely at the User's own risk.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">11. Indemnity</h2>
            <p className="text-gray-700 leading-relaxed">
              Users agree to indemnify and hold harmless 99Prop Capital from any claims, liabilities, damages, legal actions, penalties, or expenses arising out of:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Use of valuation tools</li>
              <li>Reliance on ROI projections</li>
              <li>Real estate transactions executed based on platform outputs</li>
              <li>Misuse of the Platform</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">12. Market Risk Acknowledgement</h2>
            <p className="text-gray-700 leading-relaxed">
              Users acknowledge that real estate investments involve inherent risks, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Price volatility</li>
              <li>Liquidity constraints</li>
              <li>Regulatory changes</li>
              <li>Project delays</li>
              <li>Construction risks</li>
              <li>Economic downturns</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold">
              No projection, valuation, estimate, or report provided on the Platform guarantees future performance.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">13. Modification of Terms & Algorithms</h2>
            <p className="text-gray-700 leading-relaxed">99Prop Capital reserves the right to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Modify valuation methodologies</li>
              <li>Update scoring algorithms</li>
              <li>Revise analytical tools</li>
              <li>Amend these Terms & Conditions</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              At any time without prior notice.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Continued use of the Platform constitutes acceptance of revised terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">14. Arbitration & Dispute Resolution</h2>
            <p className="text-gray-700 leading-relaxed">
              Any dispute arising out of or relating to the use of this Platform shall be referred to arbitration under the Arbitration and Conciliation Act, 1996.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The seat of arbitration shall be Ghaziabad, Uttar Pradesh.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              The decision of the arbitrator shall be final and binding on the parties.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">15. Governing Law & Jurisdiction</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms & Conditions shall be governed by and construed in accordance with the laws of India.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Subject to jurisdiction of Ghaziabad, Uttar Pradesh.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Courts of the above jurisdiction shall have exclusive authority.
            </p>
          </section>

          <div className="mt-12 p-6 bg-brand-50 rounded-xl border-l-4 border-brand-500">
            <p className="text-gray-700 font-semibold">
              Last Updated: February 2025
            </p>
            <p className="text-gray-700 mt-2">
              For any questions regarding these Terms & Conditions, please contact us at{' '}
              <a href="mailto:info@99prop.com" className="text-brand-500 hover:underline font-semibold">
                info@99prop.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
